import { ErrorMessage } from 'core/components/lib/ErrorBoundary/defaultErrorBoundaries'
import useToast from 'core/hooks/useToast'
import { Error as BaseError } from 'core/types/types'

import validateBase from './validate'

export type KeysOfUnion<T> = T extends T ? keyof T : never

export type ArrayElement<T extends ReadonlyArray<unknown>> = T extends ReadonlyArray<infer E> ? E : never

export type PartialPick<T, K extends keyof T> = {
  [P in K]?: T[K]
}

export type Maybe<T, Prop extends PropertyKey, F = T> = T extends { [P in Prop]?: infer R } ? R | undefined : F

export type PickRequired<T, K extends keyof T> = T & { [P in K]-?: T[P] }

export type DeepPartial<T> = {
  [K in keyof T]?: T[K] extends object ? DeepPartial<T[K]> : T[K]
}

export type OverrideFirstParameter<F, T> =
  F extends (first: infer A, ...args: infer P) => infer R ? (first: Omit<A, keyof T> & T, ...args: P) => R : never

export type PeachError = BaseError & {
  operation?: string
  peachRequestId?: string
}

export const isError = (e: unknown): e is PeachError =>
  !!e && typeof e === 'object' && 'status' in e && typeof e.status === 'number'

export const useErrorHandler = (operation: string) => {
  const toast = useToast()

  return (error: unknown) => {
    if (isError(error)) {
      error.operation = operation
    }

    if (isError(error) && error.status === 504) {
      toast.error(
        <>
          This request has timed out: <i>{operation}</i>. Please try again at a later time. Consider refreshing the
          page.
        </>,
      )
    } else {
      toast.error(<ErrorMessage error={error} />)
    }
  }
}

export const isClientError = (error: unknown) => isError(error) && error.status >= 400 && error.status < 500

export const isNotFoundError = (error: unknown) => isError(error) && (error.status === 404 || error.status === 422)

export const isForbiddenError = (error: unknown) => isError(error) && error.status === 403

export const validate = <T extends object | undefined>(key: string, data: T): T => {
  if (import.meta.env.VITE_ENABLE_RESPONSE_VALIDATION) {
    validateBase(key, data)
  }

  return data
}

export const isValidId = (id: string) => /^ext-|^[A-Z][A-Z0-9]-[A-Z0-9]+-[A-Z0-9]+|^\d+$/.test(id)
